import type { ExterneFreimeldung, InspectionItem, Report, ReportMetadata } from '../types';
import { selectActiveItemsForReport } from './pdf/pdfUtils';

/**
 * Die REGELN der externen Freimeldung — rein, ohne Supabase, ohne DOM
 * (docs/freimeldung-online-plan.md §F). Alles, was hier steht, ist ohne
 * Datenbank pruefbar; der Datenzugriff liegt daneben in
 * `freimeldungService.ts`.
 *
 * ZWEI FESTLEGUNGEN, die hier und nirgends sonst getroffen werden:
 *
 * (a) OFFEN HEISST OFFEN. Nur `status === 'offen'` zaehlt fuer Badges,
 *     Gruppierung und Verwaiste. Eine bestaetigte oder abgelehnte Meldung ist
 *     erledigt und darf den Koordinator nicht ein zweites Mal ansprechen.
 *
 * (b) DIE ITEM-AUSWAHL DES LINKS IST DIESELBE WIE IM PDF. Der Link traegt eine
 *     Momentaufnahme der Maengel, die im gedruckten Bericht standen —
 *     `offeneItemIdsFuerBericht` benutzt deshalb `selectActiveItemsForReport`
 *     aus pdfUtils und filtert daraus die offenen. Wer hier eine zweite
 *     Auswahl baut, zeigt dem Melder Maengel, die nicht in seinem PDF stehen
 *     (oder verschweigt welche, die drinstehen).
 */

/** Mehr Maengel traegt ein Link nicht — dieselbe Kappung wie in der RPC. */
export const MAX_ITEM_IDS = 500;

export type FreimeldungSprache = 'de' | 'en';

// ─── Freischaltung je Projekt ───────────────────────────────────────────────

/**
 * Ist die Online-Freimeldung fuer DIESES Projekt freigeschaltet?
 *
 * VORGABE IST AN (Betreiber-Entscheidung 28.10.2026, ersetzt „Vorgabe aus"
 * vom 18.09.2026): „lieber abwählen als vergessen dazuzuwählen". Ein
 * FEHLENDES Flag ist freigeschaltet — das gilt für jedes Altprojekt und für
 * jeden Anlageweg (Assistent, Angebotsannahme, CSV-Import, künftige Wege),
 * ohne dass irgendwo ein Wert geschrieben werden muss. Aus ist ein Projekt
 * nur mit dem ausdrücklichen `false`, das der abgewählte Haken schreibt.
 *
 * Gleich streng wie die RPCs ab Nr. 183: fehlt der Wert, gilt `true`; steht
 * einer da, zählt nur der Wahrheitswert `true`. Ein Bestandswert `'true'`
 * als Zeichenkette (aus einem handgeschriebenen JSON), `1` oder `null` ist
 * KEINE Freigabe — die Oberflaeche schreibt einen booleschen Wert, und ein
 * `Boolean(...)`-Zugriff haette jede nichtleere Zeichenkette, einschliesslich
 * `'false'`, als Freigabe gelesen. Deshalb nicht einfach `!== false`.
 */
export const istFreimeldungAktiv = (
  metadata: Pick<ReportMetadata, 'freimeldung'> | null | undefined,
): boolean => {
  const aktiv = metadata?.freimeldung?.aktiv;
  return aktiv === undefined || aktiv === true;
};

// ─── Auswahl fuer den Link ──────────────────────────────────────────────────

/**
 * Die Ids der Maengel, die im PDF dieses Berichts als OFFEN erscheinen — die
 * Momentaufnahme, die der Link traegt.
 *
 * Bewusst ueber `selectActiveItemsForReport`: Reihenfolge und Grundmenge
 * bleiben damit identisch zum Ausdruck. Gekappt bei MAX_ITEM_IDS, weil die
 * Datenbank dasselbe tut — sonst zeigte die oeffentliche Seite eine andere
 * Liste als der Client erwartet.
 */
export const offeneItemIdsFuerBericht = (report: Report): string[] =>
  selectActiveItemsForReport(report?.items ?? [])
    .filter((i) => i.status === 'open')
    .map((i) => i.id)
    .filter((id): id is string => typeof id === 'string' && !!id)
    .slice(0, MAX_ITEM_IDS);

// ─── Sortieren, gruppieren, zaehlen ─────────────────────────────────────────

/** Juengste Meldung zuerst. Kopiert die Liste — die Quelle bleibt unberuehrt. */
export const sortiere = (list: ExterneFreimeldung[]): ExterneFreimeldung[] =>
  [...(list ?? [])].sort((a, b) => (b?.gemeldetAt || '').localeCompare(a?.gemeldetAt || ''));

/**
 * Die OFFENEN Meldungen je Mangel-Id, jeweils juengste zuerst. Maengel ohne
 * offene Meldung tauchen gar nicht erst auf — die Karte fragt die Map und
 * bekommt `undefined`, nicht ein leeres Array.
 */
export const offeneJeItem = (list: ExterneFreimeldung[]): Map<string, ExterneFreimeldung[]> => {
  const map = new Map<string, ExterneFreimeldung[]>();
  sortiere(list).forEach((f) => {
    if (!f || f.status !== 'offen') return;
    const id = f.itemId || '';
    if (!id) return;
    const vorhanden = map.get(id);
    if (vorhanden) vorhanden.push(f);
    else map.set(id, [f]);
  });
  return map;
};

/** Wie viele Meldungen warten auf eine Entscheidung? */
export const zaehleOffene = (list: ExterneFreimeldung[]): number =>
  (list ?? []).filter((f) => f?.status === 'offen').length;

/**
 * Offene Meldungen zu Maengeln, die es nicht mehr gibt.
 *
 * Kein Fremdschluessel auf `inspection_items` (der Id-Typ steht nicht im Repo)
 * — das Aufraeumen faellt deshalb dem Client zu: Wird ein Mangel geloescht
 * oder in ein anderes Projekt verschoben, bliebe seine Meldung sonst fuer
 * immer im Zaehler stehen, ohne dass jemand sie oeffnen koennte.
 */
export const verwaiste = (
  list: ExterneFreimeldung[],
  items: InspectionItem[],
): ExterneFreimeldung[] => {
  const vorhanden = new Set((items ?? []).map((i) => i?.id).filter(Boolean));
  return sortiere(list).filter((f) => f?.status === 'offen' && !vorhanden.has(f.itemId));
};

// ─── Texte ──────────────────────────────────────────────────────────────────

/** `2026-09-16T12:00:00Z` → `16.09.2026`; unlesbares Datum bleibt, wie es kam. */
const tagDatum = (iso: string | null | undefined): string => {
  const roh = (iso || '').trim();
  if (!roh) return '';
  const ms = Date.parse(roh);
  if (Number.isNaN(ms)) return roh;
  const d = new Date(ms);
  const zwei = (n: number) => String(n).padStart(2, '0');
  return `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}.${d.getFullYear()}`;
};

/**
 * Der Kommentar, der beim Bestaetigen in den Mangel wandert.
 *
 * Er nennt IMMER Firma, Name und Tag der Meldung — auch ohne Freitext. Sonst
 * stuende spaeter im Protokoll ein geschlossener Mangel ohne jeden Hinweis
 * darauf, dass die Erledigung von aussen gemeldet und nicht vom Koordinator
 * gesehen wurde.
 */
export const freimeldungKommentar = (
  f: Pick<ExterneFreimeldung, 'firma' | 'name' | 'gemeldetAt' | 'kommentar' | 'art'>,
  lang: FreimeldungSprache = 'de',
): string => {
  const firma = (f?.firma || '').trim() || (lang === 'en' ? 'unknown company' : 'unbekannte Firma');
  const name = (f?.name || '').trim();
  const datum = tagDatum(f?.gemeldetAt);
  const wer = [name, datum].filter(Boolean).join(', ');
  const zurueck = istZurueckweisung(f);
  const kopf =
    lang === 'en'
      ? `${zurueck ? 'Online rejection' : 'Online clearance report'} by ${firma}${wer ? ` (${wer})` : ''}`
      : `${zurueck ? 'Zurückweisung online' : 'Freimeldung online'} von ${firma}${wer ? ` (${wer})` : ''}`;
  const text = (f?.kommentar || '').trim();
  return text ? `${kopf}: ${text}` : kopf;
};

/**
 * Weist die Firma den Mangel zurueck (statt ihn als behoben zu melden)?
 * Nur der ausdrueckliche Wert zaehlt; ohne `art` ist es eine Freimeldung.
 */
export const istZurueckweisung = (f: Pick<ExterneFreimeldung, 'art'> | null | undefined): boolean =>
  f?.art === 'zurueckgewiesen';

/**
 * Das Abzeichen auf der Mangelkarte. Ohne offene Meldung: kein Text.
 * Sind ALLE offenen Meldungen Zurueckweisungen, sagt es das — eine
 * Zurueckweisung ist keine Freimeldung, und der Koordinator soll schon am
 * Kartenkopf sehen, dass eine Firma widerspricht.
 */
export const badgeText = (n: number, lang: FreimeldungSprache = 'de', zurueckgewiesen = 0): string => {
  const anzahl = Number.isFinite(n) ? Math.trunc(n) : 0;
  if (anzahl <= 0) return '';
  const nurZurueck = Number.isFinite(zurueckgewiesen) && Math.trunc(zurueckgewiesen) >= anzahl;
  if (nurZurueck) {
    return lang === 'en' ? `Rejected by company (${anzahl})` : `Zurückweisung liegt vor (${anzahl})`;
  }
  return lang === 'en'
    ? `Clearance report received (${anzahl})`
    : `Freimeldung liegt vor (${anzahl})`;
};

// ─── Warteschlange: erst der Nachweis, dann die Entscheidung ────────────────

/**
 * Was die Datenbank ueber einen Mangel sagt — die Antwort von
 * `mangelServerStatus` (freimeldungService.ts), hier zu Hause, damit die REGEL
 * daneben ohne Supabase pruefbar bleibt.
 *
 * `'sonstig'` fasst alles zusammen, was weder `open` noch `closed` ist
 * (`general`, `info`, `good`); `'fehlt'` heisst: gelesen, aber keine Zeile.
 * „Nicht feststellbar" ist KEIN Status, sondern `null` daneben.
 */
export type MangelServerStatus = 'closed' | 'open' | 'sonstig' | 'fehlt';

/**
 * Darf die Meldung jetzt gebucht werden?
 *
 * Bestaetigt wird, sobald der Mangel serverseitig nicht mehr offen ist oder
 * nicht mehr existiert — `closed` (erledigt), `sonstig` (zur Information
 * herabgestuft, positiv vermerkt) und `fehlt` (geloescht oder in ein anderes
 * Projekt gezogen) sind allesamt ein Ende der Aufgabe. `open` und `null`
 * heissen WARTEN: der Mangel steht noch offen, oder es war gar nicht zu lesen.
 *
 * WARUM NICHT NUR `closed`: Zwei Wege buchten frueher an der Schlange vorbei
 * sofort, weil ihr Mangel `closed` nie erreicht — die nachtraeglich zur
 * Information erklaerte Karte und die verwaiste Meldung im Panel. Beide
 * verliessen sich damit auf den LOKALEN Stand, waehrend der Save erst Sekunden
 * spaeter lief und scheitern konnte: die Meldung stand auf „bestaetigt" (und
 * riss ueber die RPC die Geschwister mit), der Mangel serverseitig weiter
 * offen. Mit diesen drei Zustaenden fuehrt auch fuer sie ein Nachweis zum
 * Ziel, und niemand muss mehr an der Schlange vorbei.
 */
export const nachweisErlaubtBuchung = (
  status: MangelServerStatus | null | undefined,
): boolean => status === 'closed' || status === 'sonstig' || status === 'fehlt';

/**
 * Eine Entscheidung, die auf den NACHWEIS wartet, dass der Mangel wirklich
 * geschlossen in der Datenbank steht (docs/freimeldung-online-plan.md §F:
 * „erst im Client schliessen und speichern, dann entscheiden").
 *
 * WARUM SIE WARTET: `report_freimeldung_entscheiden` fasst `inspection_items`
 * nicht an. Der Mangel wird im Client geschlossen und ueber den ganz normalen
 * Autosave gespeichert. Wuerde die Meldung sofort gebucht, stuende sie nach
 * einem gescheiterten Save auf `bestaetigt`, waehrend der Mangel serverseitig
 * weiter offen ist — und der Koordinator bekaeme den Hinweis nie wieder
 * angeboten. Andersherum ist der Schaden klein: die Meldung bleibt `offen`,
 * ihr Abzeichen steht weiter an der Karte, und der naechste Versuch bucht sie.
 *
 * NICHT AUF EIN SIGNAL, SONDERN AUF EINEN NACHWEIS: Frueher loeste allein
 * `saveStatus === 'saved'` die Buchung aus. Dieses Signal gilt aber fuer den
 * ganzen Speicherlauf und nicht fuer unser Projekt — `flushAll` in
 * useOfflineSync speichert auch fremde Projekte, und deren geglueckter Save
 * haette hier eine Entscheidung bestaetigt, die nirgends angekommen ist.
 * Deshalb traegt jeder Auftrag seine `itemId`: gebucht wird erst, wenn eine
 * Lesefrage an `inspection_items` den Mangel nicht mehr als offen zurueckmeldet
 * (`mangelServerStatus` + `nachweisErlaubtBuchung`).
 *
 * DIE SCHLANGE LIEGT NUR IM SPEICHER. Ein Neuladen wirft sie weg, die Meldung
 * bleibt „offen" und das Abzeichen steht weiter an der Karte — der sichere
 * Ausgang: der Koordinator entscheidet noch einmal.
 */
export interface FreimeldungEntscheidungAuftrag {
  id: string;
  /**
   * Der Mangel, an dem die Entscheidung haengt. Ohne ihn liesse sich der
   * Nachweis nicht fuehren — ein Auftrag ohne `itemId` stuende fuer immer in
   * der Schlange, statt gebucht zu werden.
   */
  itemId: string;
  /**
   * DAS PROJEKT, FUER DAS DER AUFTRAG GILT.
   *
   * Die Schlange liegt im Modul und ueberdauert dort einen Projektwechsel.
   * Ohne dieses Kennzeichen liesse sich hinterher nicht mehr sagen, wohin ein
   * Auftrag gehoert: der Abarbeiter fragte das NEUE Projekt nach einem Mangel
   * des alten, bekaeme „nicht gefunden" und haette nichts bewiesen — oder,
   * schlimmer, traefe dort zufaellig dieselbe Id. Der Abarbeiter laesst fremde
   * Auftraege deshalb nicht laufen, sondern raeumt sie weg.
   */
  projectId: string;
  entscheidung: 'bestaetigt' | 'abgelehnt';
  kommentar?: string;
  /**
   * Wie oft der Nachweis schon vergeblich gefuehrt wurde. Fehlt die Zahl,
   * gilt sie als 0 — ein frisch eingereihter Auftrag hat es noch nie
   * versucht. Ab `FREIMELDUNG_MAX_NACHWEISE` gibt der Abarbeiter auf.
   */
  versuche?: number;
  /**
   * DIE GENERATION DES AUFTRAGS — von `warteschlangeEinreihen` vergeben,
   * streng aufsteigend, nur im Speicher.
   *
   * Zwischen dem Beginn eines Laufs und seinem Ende liegen `await`s, und in
   * dieser Luecke kann der Koordinator DIESELBE Meldung neu entscheiden: der
   * alte Auftrag wird ersetzt, der neue faengt bei `versuche = 0` an. Ohne
   * dieses Kennzeichen erbte er hinterher den vergeblichen Nachweis des alten
   * Laufs — die frische Entscheidung verloere Anlaeufe, die ihr Vorgaenger
   * verbraucht hat, und fiele im schlimmsten Fall nach einem einzigen eigenen
   * Versuch aus der Schlange. `warteschlangeVersuchZaehlen` zaehlt deshalb nur
   * gegen die Laufnummer, mit der der Lauf begonnen hat.
   */
  laufnummer?: number;
}

/**
 * Nach so vielen vergeblichen Nachweisen gibt der Abarbeiter auf.
 *
 * Vier Versuche sind an der 30-Sekunden-Uhr des Moduls rund zwei Minuten. Ist
 * der Mangel bis dahin nicht serverseitig geschlossen, wird er es meist auch
 * nicht mehr: der Save ist gescheitert, das Netz ist fort oder RLS verweigert.
 * Weiterfragen kostete dann nur noch Abfragen — und, schwerer wiegend, es
 * liesse den Koordinator im Glauben, seine Entscheidung sei unterwegs.
 *
 * AUFGEBEN IST HIER DER SICHERE AUSGANG: gebucht wurde nichts, die Meldung
 * bleibt „offen", ihr Abzeichen steht weiter an der Karte — nur muss der
 * Koordinator es erfahren, und genau dafuer gibt es
 * `freimeldungNachweisHinweis`.
 */
export const FREIMELDUNG_MAX_NACHWEISE = 4;

/**
 * Reiht einen Auftrag ein.
 *
 * Auftraege OHNE Kennung, OHNE Mangel-Id ODER OHNE Projekt fallen weg: die
 * ersten waeren ein RPC-Aufruf, der sicher scheitert, die zweiten ein
 * Nachweis, der nie zu fuehren ist, die dritten eine Lesefrage ohne Adresse.
 * Jedes Mal bleibt die Meldung „offen" und wird noch einmal angeboten — das
 * ist der sichere Ausgang. STILL bleibt der Aufrufer dabei nicht: das Modul
 * prueft dieselben drei Felder und setzt den Hinweis
 * (`freimeldungNachweisHinweis`), sonst klickte der Koordinator „Bestätigen"
 * und nichts geschaehe.
 *
 * Dieselbe Meldung zweimal entschieden — der Koordinator oeffnet den Dialog,
 * bricht ab, oeffnet erneut — steht nur EINMAL in der Schlange, und zwar mit
 * der zuletzt getroffenen Entscheidung: die frueheren hat er selbst ueberholt.
 * Der Versuchszaehler faengt dabei von vorn an, weil der neue Auftrag keinen
 * mitbringt — eine frische Entscheidung verdient ihre vollen zwei Minuten.
 *
 * DIE EINZIGE UNREINE STELLE DIESER DATEI: jeder eingereihte Auftrag bekommt
 * eine neue `laufnummer` aus einem Zaehler im Modul. Sie ist der Unterschied
 * zwischen „derselbe Auftrag" und „dieselbe Meldung, neu entschieden" — ohne
 * sie erbte die frische Entscheidung die Versuche der alten (siehe
 * `warteschlangeVersuchZaehlen`).
 */
let laufnummerZaehler = 0;

export const warteschlangeEinreihen = (
  offen: FreimeldungEntscheidungAuftrag[],
  auftrag: FreimeldungEntscheidungAuftrag,
): FreimeldungEntscheidungAuftrag[] => {
  const id = (auftrag?.id || '').trim();
  const itemId = (auftrag?.itemId || '').trim();
  const projectId = (auftrag?.projectId || '').trim();
  if (!id || !itemId || !projectId) return [...(offen ?? [])];
  laufnummerZaehler += 1;
  return [
    ...(offen ?? []).filter((a) => a?.id !== id),
    { ...auftrag, id, itemId, projectId, laufnummer: laufnummerZaehler },
  ];
};

/** Was ein vergeblicher Nachweis aus der Schlange macht. */
export interface WarteschlangeVersuchErgebnis {
  /** Die Schlange danach: der Auftrag zaehlt eins hoch — oder ist fort. */
  warteschlange: FreimeldungEntscheidungAuftrag[];
  /**
   * `true`, wenn dieser Versuch der letzte war und der Auftrag aufgegeben
   * wurde. Nur dann gehoert dem Koordinator ein Hinweis auf den Schirm.
   */
  aufgegeben: boolean;
}

/**
 * Zaehlt EINEN vergeblichen Nachweis und gibt nach `FREIMELDUNG_MAX_NACHWEISE`
 * auf.
 *
 * WARUM UEBERHAUPT GEZAEHLT WIRD: Ein Auftrag, dessen Mangel serverseitig nie
 * `closed` wird (gescheiterter Save, geloeschte Zeile, RLS), bliebe sonst bis
 * zum Neuladen der Seite in der Schlange, und die Nachfass-Uhr fragte ihn alle
 * 30 Sekunden erfolglos ab — unbegrenzt und unbemerkt.
 *
 * WARUM DAS WEGWERFEN UNGEFAEHRLICH IST: Der Auftrag ist eine ABSICHT, keine
 * Buchung. Faellt er weg, steht die Meldung unveraendert auf „offen", das
 * Abzeichen bleibt an der Karte, und der Koordinator kann dieselbe
 * Entscheidung noch einmal treffen. Verloren geht nur das stille Warten.
 *
 * NUR GEGEN DIESELBE GENERATION. Wird `laufnummer` uebergeben, zaehlt der
 * Versuch allein gegen den Auftrag, der beim Start des Laufs in der Schlange
 * stand. Hat der Koordinator waehrenddessen dieselbe Meldung neu entschieden,
 * traegt der Auftrag eine andere Laufnummer — er bleibt unangetastet bei
 * `versuche = 0` stehen, statt den vergeblichen Nachweis seines Vorgaengers zu
 * erben und dessen Kappung mitzuschleppen. Ohne `laufnummer` (Altaufrufe,
 * Auftraege aus der Zeit vor der Nummer) zaehlt wie bisher die Kennung allein.
 *
 * Rein: die uebergebene Liste bleibt unberuehrt, die uebrigen Auftraege
 * behalten ihren Platz.
 */
export const warteschlangeVersuchZaehlen = (
  offen: FreimeldungEntscheidungAuftrag[],
  id: string,
  laufnummer?: number,
): WarteschlangeVersuchErgebnis => {
  const gesucht = (id || '').trim();
  const quelle = (offen ?? []).filter((a): a is FreimeldungEntscheidungAuftrag => !!a);
  if (!gesucht) return { warteschlange: quelle, aufgegeben: false };
  let aufgegeben = false;
  const warteschlange: FreimeldungEntscheidungAuftrag[] = [];
  quelle.forEach((a) => {
    if ((a.id || '').trim() !== gesucht) {
      warteschlange.push(a);
      return;
    }
    // Ein anderer Auftrag mit derselben Kennung: der Koordinator hat waehrend
    // des Laufs neu entschieden. Der Versuch gehoert dem Vorgaenger, nicht ihm.
    if (laufnummer !== undefined && a.laufnummer !== laufnummer) {
      warteschlange.push(a);
      return;
    }
    const bisher = Number.isFinite(a.versuche) ? Math.max(0, Math.trunc(a.versuche as number)) : 0;
    const versuche = bisher + 1;
    if (versuche >= FREIMELDUNG_MAX_NACHWEISE) {
      aufgegeben = true;
      return;
    }
    warteschlange.push({ ...a, versuche });
  });
  return { warteschlange, aufgegeben };
};

/**
 * Der Klartext, wenn eine Entscheidung NICHT in der Datenbank ankam — weil der
 * Nachweis nach `FREIMELDUNG_MAX_NACHWEISE` Versuchen ausblieb oder der
 * Auftrag gar nicht erst einzureihen war.
 *
 * Er sagt beides: dass nichts gebucht wurde, und was zu tun ist. Ohne ihn
 * gaebe der Koordinator seine Entscheidung ins Leere — die Karte saehe
 * hinterher genauso aus wie vorher.
 */
export const freimeldungNachweisHinweis = (lang: FreimeldungSprache = 'de'): string =>
  lang === 'en'
    ? 'Clearance report not confirmed yet: the defect is not stored as completed on the server. Please confirm again later.'
    : 'Freimeldung noch nicht bestätigt: der Mangel ist auf dem Server noch nicht als erledigt gespeichert. Bitte später erneut bestätigen.';

// ─── Endgueltig gescheitert: nicht weiter versuchen ─────────────────────────

/**
 * DIE SQLSTATES, NACH DENEN EIN WEITERER VERSUCH NICHTS MEHR AENDERT.
 *
 *   • `P0001` — die Meldung wurde inzwischen anders entschieden. Die RPC
 *     weigert sich, eine Entscheidung zu ueberschreiben.
 *   • `P0002` — es gibt die Zeile nicht (geloescht, fremdes Projekt).
 *   • `22023` — die Eingabe ist abgelehnt (unbekannte Entscheidung, kaputte
 *     Kennung). Dieselben Argumente kaemen beim naechsten Mal wieder.
 *   • `42501` — RLS verweigert den Zugriff. Wer das Projekt nicht schreiben
 *     darf, darf es auch in zwei Minuten nicht.
 *
 * WARUM DAS UEBERHAUPT ZAEHLT: Die Warteschlange im SiGeKoInspectionModule
 * behaelt einen Auftrag, dessen RPC scheitert — genau richtig fuer ein
 * abgerissenes Netz oder eine Zeitueberschreitung, denn die naechste
 * 30-Sekunden-Runde holt die Buchung dann nach. Fuer diese vier Faelle ist es
 * ein Keil: der Auftrag bliebe fuer die ganze Sitzung in der Schlange, die Uhr
 * fragte alle 30 Sekunden erfolglos nach, und der Koordinator saehe nie, dass
 * seine Entscheidung nicht mehr zu buchen ist. Der Versuchszaehler greift hier
 * NICHT — er zaehlt nur ausgebliebene NACHWEISE, nicht gescheiterte RPCs.
 *
 * ALLES ANDERE IST WIEDERHOLBAR (kein Netz, Zeitueberschreitung, 40001,
 * 57014, PGRST202 …): dort bleibt der Auftrag stehen und wird erneut
 * versucht. Im Zweifel also weiterprobieren — ein Auftrag zu viel in der
 * Schlange kostet eine Abfrage, ein zu frueh weggeworfener eine Entscheidung.
 */
export const ENDGUELTIGE_ENTSCHEIDUNGSFEHLER: readonly string[] = [
  'P0001',
  'P0002',
  '22023',
  '42501',
];

/** Ist dieser SQLSTATE einer, nach dem die Schlange aufgeben muss? */
export const istEndgueltigerEntscheidungsfehler = (code: string | null | undefined): boolean => {
  const c = (code || '').trim().toUpperCase();
  return !!c && ENDGUELTIGE_ENTSCHEIDUNGSFEHLER.includes(c);
};

/**
 * Der Klartext, wenn die Entscheidung endgueltig nicht mehr zu buchen ist.
 *
 * Er nennt den Grund aus der Datenbank mit, weil die vier Faelle verschiedene
 * Antworten verlangen: „bereits anders entschieden" heisst nachsehen,
 * „kein Zugriff" heisst den Innendienst fragen. Ohne Grund bliebe nur ein
 * Satz, der nichts erklaert.
 */
export const freimeldungEndgueltigHinweis = (
  klartext: string,
  lang: FreimeldungSprache = 'de',
): string => {
  const grund = (klartext || '').trim();
  return lang === 'en'
    ? `Decision is no longer possible${grund ? `: ${grund}` : '.'}`
    : `Entscheidung nicht mehr möglich${grund ? `: ${grund}` : '.'}`;
};

// ─── Der Hinweis EINES Laufs ────────────────────────────────────────────────

/**
 * Was ein Lauf der Warteschlange zu berichten hat — alles davon, nicht das
 * Dringlichste davon.
 *
 * DER BEFUND, GEGEN DEN DAS STEHT: Frueher gewann EIN Satz je Lauf
 * (endgueltig vor wiederholbar vor aufgegeben). Ein Lauf kann aber mehrere
 * Auftraege tragen, und die scheitern verschieden: Auftrag A faellt an der
 * Kappung aus der Schlange (`aufgegeben` — er wird NIE wieder versucht),
 * waehrend B an einem abgerissenen Netz haengt (`wiederholbar` — er bleibt
 * eingereiht). Der Koordinator las dann allein „… Die App versucht es
 * erneut." und hielt beide fuer unterwegs; dass A still weggefallen war,
 * sagte ihm niemand. Genau davor sollte `freimeldungNachweisHinweis` bewahren.
 *
 * DESHALB: jeder zutreffende Teil kommt in den Satz, in der Reihenfolge, in
 * der er drueckt — was NIE mehr geht, dann was aufgegeben wurde, zuletzt was
 * die App selbst nachholt. Doppelter Wortlaut faellt weg (zwei Auftraege
 * derselben Ursache sagen denselben Satz, und zweimal gelesen erklaert er
 * nicht mehr als einmal).
 *
 * LEER HEISST NICHT „ALLES GUT", sondern nur „nichts zu melden". Ob der
 * Hinweis daraufhin WEGGERAEUMT werden darf, entscheidet der Aufrufer: er
 * allein weiss, ob der Lauf wirklich sauber durchlief oder ob noch etwas in
 * der Schlange steht.
 */
export interface FreimeldungLaufBefund {
  /**
   * Klartext einer Entscheidung, die endgueltig nicht mehr zu buchen ist
   * (P0001, P0002, 22023, 42501) — der Grund aus der Datenbank.
   */
  endgueltig?: string | null;
  /**
   * Mindestens ein Auftrag ist nach `FREIMELDUNG_MAX_NACHWEISE` vergeblichen
   * Nachweisen aus der Schlange gefallen.
   */
  aufgegeben?: boolean;
  /**
   * Klartext eines wiederholbaren Fehlschlags (kein Netz, Zeitueberschreitung)
   * — der Auftrag bleibt eingereiht, die Nachfass-Uhr holt ihn nach.
   */
  wiederholbar?: string | null;
  lang?: FreimeldungSprache;
}

/**
 * Die Zusage, die NUR an den wiederholbaren Klartext gehoert: er kommt aus
 * `entscheideFreimeldungMitBudget` und verspricht von sich aus nichts ueber
 * einen weiteren Versuch — die Warteschlange tut es.
 */
const wiederholungsZusage = (lang: FreimeldungSprache): string =>
  lang === 'en' ? 'The app will try again.' : 'Die App versucht es erneut.';

export const freimeldungLaufHinweis = (befund?: FreimeldungLaufBefund): string => {
  const lang: FreimeldungSprache = befund?.lang === 'en' ? 'en' : 'de';
  const endgueltig = (befund?.endgueltig || '').trim();
  const wiederholbar = (befund?.wiederholbar || '').trim();
  const teile: string[] = [];
  if (endgueltig) teile.push(freimeldungEndgueltigHinweis(endgueltig, lang));
  if (befund?.aufgegeben) teile.push(freimeldungNachweisHinweis(lang));
  if (wiederholbar) teile.push(`${wiederholbar} ${wiederholungsZusage(lang)}`);
  const gesehen = new Set<string>();
  return teile
    .filter((t) => {
      if (gesehen.has(t)) return false;
      gesehen.add(t);
      return true;
    })
    .join(' ');
};

/**
 * Nimmt die Auftraege aus der Schlange, die WIRKLICH gebucht wurden — und nur
 * die.
 *
 * DER BEFUND, GEGEN DEN DAS STEHT: Der Abarbeiter strich frueher jeden
 * Auftrag, den er angefasst hatte. `entscheiden` im Hook faengt seine Fehler
 * aber ab und laeuft danach ganz normal weiter; ein gescheiterter RPC — kein
 * Netz, RLS, abgelaufene Sitzung — sah deshalb aus wie eine geglueckte
 * Buchung. Der Auftrag verschwand, die Schlange war leer, die Nachfass-Uhr
 * hielt an (sie laeuft nur, solange etwas wartet), und die Entscheidung des
 * Koordinators war fort, ohne je in der Datenbank angekommen zu sein.
 *
 * Deshalb entscheidet hier die Liste der TATSAECHLICH gebuchten Kennungen,
 * nicht die der versuchten: Was nicht darin steht, bleibt stehen und wird
 * beim naechsten Durchlauf erneut versucht. Der Preis eines Irrtums in diese
 * Richtung ist klein (eine Lesefrage und ein zweiter RPC), der in die andere
 * Richtung teuer (eine verlorene Entscheidung).
 *
 * Gefiltert wird gegen den AKTUELLEN Stand der Schlange, den der Aufrufer
 * uebergibt — waehrend des Abarbeitens kann ein neuer Auftrag eingereiht
 * worden sein, und der darf nicht mit weggeraeumt werden.
 */
export const warteschlangeBereinigen = (
  offen: FreimeldungEntscheidungAuftrag[],
  gebucht: Iterable<string> | null | undefined,
): FreimeldungEntscheidungAuftrag[] => {
  const erledigt = new Set<string>();
  for (const id of gebucht ?? []) {
    const k = (id || '').trim();
    if (k) erledigt.add(k);
  }
  return (offen ?? []).filter((a) => !!a && !erledigt.has((a.id || '').trim()));
};

// ─── Link ───────────────────────────────────────────────────────────────────

/**
 * Der oeffentliche Link. `pathname` bleibt drin, weil die App nicht zwingend
 * an der Wurzel liegt; der Hash-Router (`#/freimeldung/<token>`) liegt neben
 * `#/einweisung/` und `#/unterweisung/` in index.tsx.
 */
export const freimeldungLinkUrl = (origin: string, pathname: string, token: string): string =>
  `${origin}${pathname || '/'}#/freimeldung/${encodeURIComponent(token)}`;
